import { Camera, Eye, Pencil } from 'lucide-react';
import { Avatar, type AvatarSize } from '../Avatar';
import { Menu, type MenuItem } from '../Menu';

/**
 * A record's avatar, with a **Photo** menu that appears over it on hover and on focus.
 *
 * One implementation, because there is now more than one place a profile shows its subject's
 * face: the sidebar layout's `<ProfileIdentity>` and the header strip's avatar slot. A second
 * copy of this markup is how the two would end up with different scrims, different reveal
 * triggers, or an accessible name on one and not the other.
 *
 * **The overlay opens the shared `<Menu>`, never a hand-rolled dropdown.** It started as a
 * single Edit button and grew a second verb the moment anyone wanted to look at the picture
 * rather than replace it — which is exactly the shape the settings cog exists for, and the
 * reason that rule says to reach for the menu on the first action rather than the second.
 *
 * **An absent callback is what removes its entry** — hide, don't disable. The caller passes
 * `onEdit` only when the reader holds the key that writes the image, and the face still
 * renders for everyone who can read the record. With neither callback there is no overlay at
 * all, which is what makes an avatar read-only.
 */
export function EditableAvatar({
  id,
  name,
  email,
  imageUrl,
  size = '2xl',
  onEdit,
  onPreview,
}: {
  /** Required once the menu exists — `<Menu>` needs a stable id for its panel. */
  id: string;
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  size?: AvatarSize;
  onEdit?: () => void;
  onPreview?: () => void;
}) {
  const items: MenuItem[] = [];

  if (onEdit) {
    items.push({
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
      onClick: onEdit,
    });
  }

  if (onPreview) {
    items.push({
      key: 'preview',
      label: 'Preview',
      icon: <Eye className="h-4 w-4" aria-hidden="true" />,
      onClick: onPreview,
      /*
        Disabled for STATE, with the reason — there is nothing to enlarge when the disc is
        drawing initials. Not hidden: the entry vanishing would read as a reader who may not
        look at the photograph, which is a different thing and not true.
      */
      disabledReason: imageUrl ? null : 'No photograph yet.',
    });
  }

  if (items.length === 0) {
    return <Avatar name={name} email={email} imageUrl={imageUrl} size={size} />;
  }

  return (
    <Menu
      id={id}
      items={items}
      align="left"
      trigger={({ open, toggle }) => (
        <span className="group relative inline-flex shrink-0">
          <Avatar name={name} email={email} imageUrl={imageUrl} size={size} />

          {/*
            Revealed on hover AND on focus — `group-focus-within`, which is how the tree's lane
            actions already do it. A control that only appears under a pointer is unreachable
            by keyboard, so this is `opacity-0` rather than `hidden`: it stays in the tab run
            and paints the moment focus reaches it.

            It also stays painted while the menu is OPEN. Fading the trigger out from under an
            open panel leaves the reader with a menu attached to nothing — the pointer has
            usually left the disc by the time they reach the second entry.

            The scrim is slate-900/65 because the ground under it is a photograph nobody chose:
            over the lightest possible image the white label still clears AA at about 6:1,
            where a lighter scrim would fall under it on exactly the pictures that need it
            most.
          */}
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={open}
            className={`absolute inset-0 flex items-center justify-center gap-1 rounded-full bg-slate-900/65 text-xs font-medium text-white transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 ${
              open ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/*
              The icon is dropped on the smaller discs: under 56px it and the word together
              leave neither legible, and the word is the half that says what this opens.
            */}
            {size === 'xl' || size === '2xl' ? (
              <Camera className="h-3.5 w-3.5" aria-hidden="true" />
            ) : null}
            Photo
            {/* The accessible name CONTAINS the visible label, which is what WCAG 2.5.3 asks
                for — an `aria-label` of "Photo options" would replace "Photo" rather than
                extend it, and voice control would then name something nobody can see. */}
            <span className="sr-only"> options</span>
          </button>
        </span>
      )}
    />
  );
}
